import { flexRender, SortDirection } from '@tanstack/react-table';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import React from 'react';
import { SimpleTableTWProps } from './SimpleTableTW';
import { Show } from '../../../components/shared/Show';
import { IHasTestData } from '../../../ui-toolkit/test-utilities/IHasTestData';
import classNames from 'classnames';
import { mergeTests } from '../../../ui-toolkit/test-utilities/mergeTests';

interface IGenericHeaderTitle {
  headerTitle: string;
  totalColumns: number;
}

const GenericHeaderTitle = ({ headerTitle, totalColumns }: IGenericHeaderTitle) => (
  <tr>
    <th
      colSpan={totalColumns}
      className="text-white-700 shadow-inset border-b border-gray-300 bg-white px-5 py-2 text-left text-sm font-semibold"
    >
      {headerTitle}
    </th>
  </tr>
);

export function SimpleTableHeader<T>({ table, ...rest }: SimpleTableTWProps<T>) {
  return (
    <thead className={'bg-gray-50'}>
      {rest.headerTitle && (
        <GenericHeaderTitle headerTitle={rest.headerTitle} totalColumns={table.getHeaderGroups()[0].headers.length} />
      )}
      {table.getHeaderGroups().map((headerGroup) => (
        <tr key={headerGroup.id}>
          {headerGroup.headers.map((header) => (
            <th key={header.id} className={'select-none px-6 py-4 text-left text-sm font-thin text-gray-500'}>
              <Show when={!header.isPlaceholder}>
                {flexRender(header.column.columnDef.header, header.getContext())}
              </Show>
            </th>
          ))}
        </tr>
      ))}
    </thead>
  );
}

interface ChevronPickerProps extends IHasTestData {
  sortState: false | SortDirection;
}
const ChevronPicker: React.FC<ChevronPickerProps> = ({ sortState, ...rest }) => {
  if (!sortState)
    return (
      <div className={'h-5 w-5'} data-test-id={mergeTests(rest['data-test-id'], 'sortable-table-header-no-chevron')} />
    );
  if (sortState === 'asc') {
    return (
      <ChevronUpIcon
        className={'h-5 w-5'}
        data-test-id={mergeTests(rest['data-test-id'], 'sortable-table-header-up-chevron')}
      />
    );
  }
  return (
    <ChevronDownIcon
      className={'h-5 w-5'}
      data-test-id={mergeTests(rest['data-test-id'], 'sortable-table-header-down-chevron')}
    />
  );
};

export function SortableTableHeader<T>({ table, ...rest }: SimpleTableTWProps<T>) {
  return (
    <thead className={'bg-gray-50'}>
      {rest.headerTitle && (
        <GenericHeaderTitle headerTitle={rest.headerTitle} totalColumns={table.getHeaderGroups()[0].headers.length} />
      )}
      {table.getHeaderGroups().map((headerGroup) => (
        <tr key={headerGroup.id}>
          {headerGroup.headers.map((header) => (
            <th
              key={header.id}
              colSpan={header.colSpan}
              className={'select-none px-6 py-4 text-left text-sm font-thin text-gray-500'}
            >
              <Show when={!header.isPlaceholder}>
                <div
                  className={classNames('flex flex-row', {
                    'cursor-pointer': header.column.getCanSort(),
                  })}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  <ChevronPicker sortState={header.column.getIsSorted()} data-test-id={header.column.id} />
                </div>
              </Show>
            </th>
          ))}
        </tr>
      ))}
    </thead>
  );
}
